import { useIntersectionObserver } from "@faire/web--source/ui/hooks/useIntersectionObserver";
import React, { useCallback } from "react";

interface IOptions {
  /**
   * Margin around the root. Can have values similar to the CSS margin property, e.g. "10px 20px 30px 40px"
   * (top, right, bottom, left). The values can be percentages. This set of values serves to grow or shrink each side
   * of the root element's bounding box before computing intersections. Defaults to all zeros.
   */
  rootMargin?: string;
  /**
   * Either a single number or an array of numbers which indicate at what percentage of the target's visibility
   * the observer's callback should be executed. If you only want to detect when visibility passes the 50% mark,
   * you can use a value of 0.5. If you want the callback to run every time visibility passes another 25%, you would
   * specify the array [0, 0.25, 0.5, 0.75, 1]. The default is 0 (meaning as soon as even one pixel is visible,
   * the callback will be run). A value of 1.0 means that the threshold isn't considered passed until every pixel
   * is visible.
   */
  threshold?: number | number[];
}

/**
 * Use it to when you need an event to be fired when element becomes visible.
 * Note ATM this does not support inner scroll views, and only considers when things come into the window view only.
 * Usage
 * ```tsx
 * const MyComponent = () => {
 *     const elementRef = useDoActionWhenVisible((entry) => { ... do action when visible ...}, options)
 *
 *     return <div ref={elementRef} />
 * }
 * ```
 * @param doActionCallback
 * @param options
 */
export const useDoActionWhenVisible = <E extends Element>(
  doActionCallback:
    | undefined
    | null
    | ((entry: IntersectionObserverEntry) => void),
  options: Readonly<IOptions> = {
    threshold: 0,
    rootMargin: "0px",
  }
): React.RefCallback<E | undefined> => {
  const refCallback = useIntersectionObserver((entry, observer) => {
    if (entry.isIntersecting && typeof doActionCallback === "function") {
      doActionCallback(entry);
      // manually disconnect the observer to avoid multiple calls
      observer.disconnect();
    }
  }, options);

  return useCallback(
    (element: E | null | undefined) => {
      refCallback(element);
    },
    [refCallback]
  );
};
